import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { User, CreateUser, UpdateUser, LoginRequest, AuthResponse, Role, CreateRole, UpdateRole, Permission, CreatePermission, UpdatePermission, UserAuditLog, RoleAuditLog, CostLibraryItem, CostLibraryFilter, Category, CreateCategory, UpdateCategory, PagedResult, UserFilter, RoleFilter, CostItemDetail, CreateCostItem, UpdateCostItem, Unit, CreateUnit, UpdateUnit, CostItemGroup, CreateCostItemGroup, UpdateCostItemGroup, Supplier, Location, Client, Project, Estimate, EstimateList, EstimateItem, CreateProject, UpdateProject, CreateEstimate, UpdateEstimate, CreateEstimateItem, UpdateEstimateItem, UploadAutocadResponse, DxfLayerRule, CreateDxfLayerRule, UpdateDxfLayerRule, EstimateActivity, DocumentArchiveListItem, DocumentArchiveDetail, DocumentArchiveStats, DocumentArchiveCustodianLog, CustodianOption, ShareableUser, VaultStatus, VaultAuthorizeResponse, DriveFolderEntry, UpdateDocumentArchiveMetadataRequest, ChatStatusDto, ChatSkillDto, ChatSessionDto, ChatMessageDto, ChatMessagesPageDto, ChatSendResponseDto, CreateChatSessionDto, RenameChatSessionDto, UpdateChatSessionSkillsDto, UpdateChatSessionModelDto } from '../types/api';

/** Base URL for the API, including `/api` (controllers use `Route("api/...")`). */
export function resolveApiBaseUrl(): string {
  const fromEnv = process.env.REACT_APP_API_URL;

  // Relative path — same origin via HTTPS proxy (works on localhost or LAN IP).
  if (fromEnv?.startsWith('/')) return fromEnv;

  // Page loaded over HTTPS: always use same-origin /api (avoids mixed content and stale
  // absolute http://LAN:30080 URLs baked into old cached JS bundles).
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return '/api';
  }

  // Production builds are served behind nginx; ignore stale absolute URLs baked in at build time.
  if (process.env.NODE_ENV === 'production') {
    return '/api';
  }

  if (fromEnv) return fromEnv;
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:59260/api';
  }
  throw new Error(
    'REACT_APP_API_URL is not set. Use ui/.env.development or see docs/UI-API-INTEGRATION.md'
  );
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: resolveApiBaseUrl(),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        if (config.data instanceof FormData) {
          delete (config.headers as Record<string, unknown>)['Content-Type'];
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.post('/auth/login', credentials);
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await this.api.post('/auth/logout');
    } catch (error) {
      // Log the error but don't throw it - we still want to clear local storage
      console.error('Logout API call failed:', error);
    }
    // Note: We don't clear localStorage here - let the component handle that
    // The token needs to be available for the API call
  }

  async validateToken(): Promise<boolean> {
    try {
      await this.api.post('/auth/validate');
      return true;
    } catch {
      return false;
    }
  }

  // User endpoints
  async getUsers(filter: UserFilter = {}): Promise<PagedResult<User>> {
    const params = new URLSearchParams();
    if (filter.searchTerm) params.append('searchTerm', filter.searchTerm);
    if (filter.roleId !== undefined) params.append('roleId', filter.roleId.toString());
    if (filter.isActive !== undefined) params.append('isActive', filter.isActive.toString());
    if (filter.pageNumber) params.append('pageNumber', filter.pageNumber.toString());
    if (filter.pageSize) params.append('pageSize', filter.pageSize.toString());
    if (filter.sortBy) params.append('sortBy', filter.sortBy);
    if (filter.sortDescending !== undefined) params.append('sortDescending', filter.sortDescending.toString());

    const response: AxiosResponse<PagedResult<User>> = await this.api.get(
      `/users?${params.toString()}`
    );
    return response.data;
  }

  async getUser(id: number): Promise<User> {
    const response: AxiosResponse<User> = await this.api.get(`/users/${id}`);
    return response.data;
  }

  async createUser(user: CreateUser): Promise<User> {
    const response: AxiosResponse<User> = await this.api.post('/users', user);
    return response.data;
  }

  async updateUser(id: number, user: UpdateUser): Promise<User> {
    const response: AxiosResponse<User> = await this.api.put(`/users/${id}`, user);
    return response.data;
  }

  async deleteUser(id: number): Promise<void> {
    await this.api.delete(`/users/${id}`);
  }

  // Role endpoints
  async getRoles(filter: RoleFilter = {}): Promise<PagedResult<Role>> {
    const params = new URLSearchParams();
    if (filter.searchTerm) params.append('searchTerm', filter.searchTerm);
    if (filter.pageNumber) params.append('pageNumber', filter.pageNumber.toString());
    if (filter.pageSize) params.append('pageSize', filter.pageSize.toString());
    if (filter.sortBy) params.append('sortBy', filter.sortBy);
    if (filter.sortDescending !== undefined) params.append('sortDescending', filter.sortDescending.toString());

    const response: AxiosResponse<PagedResult<Role>> = await this.api.get(
      `/roles?${params.toString()}`
    );
    return response.data;
  }

  async getRole(id: number): Promise<Role> {
    const response: AxiosResponse<Role> = await this.api.get(`/roles/${id}`);
    return response.data;
  }

  async createRole(role: CreateRole): Promise<Role> {
    const response: AxiosResponse<Role> = await this.api.post('/roles', role);
    return response.data;
  }

  async updateRole(id: number, role: UpdateRole): Promise<Role> {
    const response: AxiosResponse<Role> = await this.api.put(`/roles/${id}`, role);
    return response.data;
  }

  async deleteRole(id: number): Promise<void> {
    await this.api.delete(`/roles/${id}`);
  }

  // Permission endpoints
  async getPermissions(): Promise<Permission[]> {
    const response: AxiosResponse<Permission[]> = await this.api.get('/permissions');
    return response.data;
  }

  async getPermission(id: number): Promise<Permission> {
    const response: AxiosResponse<Permission> = await this.api.get(`/permissions/${id}`);
    return response.data;
  }

  async createPermission(permission: CreatePermission): Promise<Permission> {
    const response: AxiosResponse<Permission> = await this.api.post('/permissions', permission);
    return response.data;
  }

  async updatePermission(id: number, permission: UpdatePermission): Promise<Permission> {
    const response: AxiosResponse<Permission> = await this.api.put(`/permissions/${id}`, permission);
    return response.data;
  }

  async deletePermission(id: number): Promise<void> {
    await this.api.delete(`/permissions/${id}`);
  }

  // Audit Log endpoints
  async getUserAuditLogs(userId: number): Promise<UserAuditLog[]> {
    const response: AxiosResponse<UserAuditLog[]> = await this.api.get(`/users/${userId}/auditlogs`);
    return response.data;
  }

  async getRoleAuditLogs(roleId: number): Promise<RoleAuditLog[]> {
    const response: AxiosResponse<RoleAuditLog[]> = await this.api.get(`/roles/${roleId}/auditlogs`);
    return response.data;
  }

  // Cost Library endpoints
  async getCostLibraryItems(filter: CostLibraryFilter = {}): Promise<PagedResult<CostLibraryItem>> {
    const params = new URLSearchParams();
    if (filter.categoryId) params.append('categoryId', filter.categoryId.toString());
    if (filter.searchTerm) params.append('searchTerm', filter.searchTerm);
    if (filter.pageNumber) params.append('pageNumber', filter.pageNumber.toString());
    if (filter.pageSize) params.append('pageSize', filter.pageSize.toString());
    if (filter.sortBy) params.append('sortBy', filter.sortBy);
    if (filter.sortDescending !== undefined) params.append('sortDescending', filter.sortDescending.toString());

    const response: AxiosResponse<PagedResult<CostLibraryItem>> = await this.api.get(
      `/costlibrary/items?${params.toString()}`
    );
    return response.data;
  }

  async getCategories(): Promise<Category[]> {
    const response: AxiosResponse<Category[]> = await this.api.get('/costlibrary/categories');
    return response.data;
  }

  async getCostItem(id: number): Promise<CostItemDetail> {
    const response: AxiosResponse<CostItemDetail> = await this.api.get(`/costlibrary/items/${id}`);
    return response.data;
  }

  async createCostItem(item: CreateCostItem): Promise<CostItemDetail> {
    const response: AxiosResponse<CostItemDetail> = await this.api.post('/costlibrary/items', item);
    return response.data;
  }

  async updateCostItem(id: number, item: UpdateCostItem): Promise<CostItemDetail> {
    const response: AxiosResponse<CostItemDetail> = await this.api.put(`/costlibrary/items/${id}`, item);
    return response.data;
  }

  async getUnits(): Promise<Unit[]> {
    const response: AxiosResponse<Unit[]> = await this.api.get('/costlibrary/units');
    return response.data;
  }

  async getCostItemGroups(categoryId?: number): Promise<CostItemGroup[]> {
    const params = new URLSearchParams();
    if (categoryId !== undefined) params.append('categoryId', categoryId.toString());
    const response: AxiosResponse<CostItemGroup[]> = await this.api.get(
      `/costlibrary/costitemgroups?${params.toString()}`
    );
    return response.data;
  }

  async getSuppliers(): Promise<Supplier[]> {
    const response: AxiosResponse<Supplier[]> = await this.api.get('/suppliers');
    return response.data;
  }

  // Locations
  async getLocations(): Promise<Location[]> {
    const response: AxiosResponse<Location[]> = await this.api.get('/locations');
    return response.data;
  }

  // Clients
  async getClients(): Promise<Client[]> {
    const response: AxiosResponse<Client[]> = await this.api.get('/clients');
    return response.data;
  }

  // Projects
  async getProjects(): Promise<Project[]> {
    const response: AxiosResponse<Project[]> = await this.api.get('/projects');
    return response.data;
  }

  async getProject(id: string): Promise<Project> {
    const response: AxiosResponse<Project> = await this.api.get(`/projects/${id}`);
    return response.data;
  }

  async getProjectEstimates(projectId: string): Promise<EstimateList[]> {
    const response: AxiosResponse<EstimateList[]> = await this.api.get(`/projects/${projectId}/estimates`);
    return response.data;
  }

  async createProject(project: CreateProject): Promise<Project> {
    const response: AxiosResponse<Project> = await this.api.post('/projects', project);
    return response.data;
  }

  async updateProject(id: string, project: UpdateProject): Promise<Project> {
    const response: AxiosResponse<Project> = await this.api.put(`/projects/${id}`, project);
    return response.data;
  }

  async deleteProject(id: string): Promise<void> {
    await this.api.delete(`/projects/${id}`);
  }

  // Estimates
  async getEstimates(projectId?: string): Promise<EstimateList[]> {
    const params = new URLSearchParams();
    if (projectId != null) params.append('projectId', projectId);
    const response: AxiosResponse<EstimateList[]> = await this.api.get(`/estimates?${params.toString()}`);
    return response.data;
  }

  async getEstimate(id: string): Promise<Estimate> {
    const response: AxiosResponse<Estimate> = await this.api.get(`/estimates/${id}`);
    return response.data;
  }

  async createEstimate(estimate: CreateEstimate): Promise<Estimate> {
    const response: AxiosResponse<Estimate> = await this.api.post('/estimates', estimate);
    return response.data;
  }

  async updateEstimate(id: string, estimate: UpdateEstimate): Promise<Estimate> {
    const response: AxiosResponse<Estimate> = await this.api.put(`/estimates/${id}`, estimate);
    return response.data;
  }

  async deleteEstimate(id: number): Promise<void> {
    await this.api.delete(`/estimates/${id}`);
  }

  async getEstimateItems(estimateId: string): Promise<EstimateItem[]> {
    const response: AxiosResponse<EstimateItem[]> = await this.api.get(`/estimates/${estimateId}/items`);
    return response.data;
  }

  async addEstimateItem(estimateId: string, item: CreateEstimateItem): Promise<EstimateItem> {
    const response: AxiosResponse<EstimateItem> = await this.api.post(`/estimates/${estimateId}/items`, item);
    return response.data;
  }

  async updateEstimateItem(estimateId: string, itemId: string, item: UpdateEstimateItem): Promise<EstimateItem> {
    const response: AxiosResponse<EstimateItem> = await this.api.put(`/estimates/${estimateId}/items/${itemId}`, item);
    return response.data;
  }

  async deleteEstimateItem(estimateId: string, itemId: string): Promise<void> {
    await this.api.delete(`/estimates/${estimateId}/items/${itemId}`);
  }

  async uploadAutocad(
    projectId: string,
    file: File,
    drawingUnitScale?: number,
    estimateId?: string
  ): Promise<UploadAutocadResponse> {
    const formData = new FormData();
    formData.append('projectId', projectId);
    formData.append('file', file);
    if (drawingUnitScale != null && drawingUnitScale > 0) {
      formData.append('drawingUnitScale', String(drawingUnitScale));
    }
    if (estimateId != null) {
      formData.append('estimateId', String(estimateId));
    }
    const response: AxiosResponse<UploadAutocadResponse> = await this.api.post('/estimates/upload-autocad', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async getEstimateActivity(estimateId: string): Promise<EstimateActivity[]> {
    const response: AxiosResponse<EstimateActivity[]> = await this.api.get(`/estimates/${estimateId}/activity`);
    return response.data;
  }

  async getDxfLayerRules(dxfCode?: string): Promise<DxfLayerRule[]> {
    const params = new URLSearchParams();
    if (dxfCode) params.append('dxfCode', dxfCode);
    const qs = params.toString();
    const response: AxiosResponse<DxfLayerRule[]> = await this.api.get(`/dxf-layer-rules${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  async getDxfLayerRule(id: number): Promise<DxfLayerRule> {
    const response: AxiosResponse<DxfLayerRule> = await this.api.get(`/dxf-layer-rules/${id}`);
    return response.data;
  }

  async createDxfLayerRule(rule: CreateDxfLayerRule): Promise<DxfLayerRule> {
    const response: AxiosResponse<DxfLayerRule> = await this.api.post('/dxf-layer-rules', rule);
    return response.data;
  }

  async updateDxfLayerRule(id: number, rule: UpdateDxfLayerRule): Promise<DxfLayerRule> {
    const response: AxiosResponse<DxfLayerRule> = await this.api.put(`/dxf-layer-rules/${id}`, rule);
    return response.data;
  }

  async deleteDxfLayerRule(id: number): Promise<void> {
    await this.api.delete(`/dxf-layer-rules/${id}`);
  }

  // Category endpoints (Master Data Management)
  async getAllCategories(includeInactive: boolean = false): Promise<Category[]> {
    const params = new URLSearchParams();
    if (includeInactive) params.append('includeInactive', 'true');
    const response: AxiosResponse<Category[]> = await this.api.get(
      `/categories?${params.toString()}`
    );
    return response.data;
  }

  async getCategory(id: number): Promise<Category> {
    const response: AxiosResponse<Category> = await this.api.get(`/categories/${id}`);
    return response.data;
  }

  async createCategory(category: CreateCategory): Promise<Category> {
    const response: AxiosResponse<Category> = await this.api.post('/categories', category);
    return response.data;
  }

  async updateCategory(id: number, category: UpdateCategory): Promise<Category> {
    const response: AxiosResponse<Category> = await this.api.put(`/categories/${id}`, category);
    return response.data;
  }

  async deleteCategory(id: number): Promise<void> {
    await this.api.delete(`/categories/${id}`);
  }

  // Group endpoints (Master Data Management)
  async getAllGroups(categoryId?: number, includeInactive: boolean = false): Promise<CostItemGroup[]> {
    const params = new URLSearchParams();
    if (categoryId !== undefined) params.append('categoryId', categoryId.toString());
    if (includeInactive) params.append('includeInactive', 'true');
    const response: AxiosResponse<CostItemGroup[]> = await this.api.get(
      `/groups?${params.toString()}`
    );
    return response.data;
  }

  async getGroup(id: number): Promise<CostItemGroup> {
    const response: AxiosResponse<CostItemGroup> = await this.api.get(`/groups/${id}`);
    return response.data;
  }

  async createGroup(group: CreateCostItemGroup): Promise<CostItemGroup> {
    const response: AxiosResponse<CostItemGroup> = await this.api.post('/groups', group);
    return response.data;
  }

  async updateGroup(id: number, group: UpdateCostItemGroup): Promise<CostItemGroup> {
    const response: AxiosResponse<CostItemGroup> = await this.api.put(`/groups/${id}`, group);
    return response.data;
  }

  async deleteGroup(id: number): Promise<void> {
    await this.api.delete(`/groups/${id}`);
  }

  // Unit endpoints (Master Data Management)
  async getAllUnits(includeInactive: boolean = false): Promise<Unit[]> {
    const params = new URLSearchParams();
    if (includeInactive) params.append('includeInactive', 'true');
    const response: AxiosResponse<Unit[]> = await this.api.get(
      `/units?${params.toString()}`
    );
    return response.data;
  }

  async getUnit(id: number): Promise<Unit> {
    const response: AxiosResponse<Unit> = await this.api.get(`/units/${id}`);
    return response.data;
  }

  async createUnit(unit: CreateUnit): Promise<Unit> {
    const response: AxiosResponse<Unit> = await this.api.post('/units', unit);
    return response.data;
  }

  async updateUnit(id: number, unit: UpdateUnit): Promise<Unit> {
    const response: AxiosResponse<Unit> = await this.api.put(`/units/${id}`, unit);
    return response.data;
  }

  async deleteUnit(id: number): Promise<void> {
    await this.api.delete(`/units/${id}`);
  }

  // Document Archives
  async getDocumentArchives(params: {
    searchTerm?: string;
    pageNumber?: number;
    pageSize?: number;
    sortBy?: string;
    sortDescending?: boolean;
  }): Promise<PagedResult<DocumentArchiveListItem>> {
    const response: AxiosResponse<PagedResult<DocumentArchiveListItem>> = await this.api.get('/document-archives', {
      params,
    });
    return response.data;
  }

  async getDocumentArchiveStats(): Promise<DocumentArchiveStats> {
    const response: AxiosResponse<DocumentArchiveStats> = await this.api.get('/document-archives/stats');
    return response.data;
  }

  async getDocumentArchive(id: number): Promise<DocumentArchiveDetail> {
    const response: AxiosResponse<DocumentArchiveDetail> = await this.api.get(`/document-archives/${id}`);
    return response.data;
  }

  async getCustodianOptions(): Promise<CustodianOption[]> {
    const response: AxiosResponse<CustodianOption[]> = await this.api.get('/document-archives/custodian-options');
    return response.data;
  }

  async getDocumentArchiveShareableUsers(): Promise<ShareableUser[]> {
    const response: AxiosResponse<ShareableUser[]> = await this.api.get('/document-archives/shareable-users');
    return response.data;
  }

  async getVaultStatus(): Promise<VaultStatus> {
    const response: AxiosResponse<VaultStatus> = await this.api.get('/document-archives/vault/status');
    return response.data;
  }

  async getVaultAuthorizeUrl(): Promise<VaultAuthorizeResponse> {
    const response: AxiosResponse<VaultAuthorizeResponse> = await this.api.get('/document-archives/vault/authorize');
    return response.data;
  }

  /**
   * Create a document archive. Supply `documentName` when not uploading a file, or to override the name used for storage.
   * `targetFolderSubPath` is relative to the default archive root (e.g. `Q1/Reports`).
   */
  async uploadDocumentArchive(
    file: File | null | undefined,
    options?: {
      documentName?: string;
      projectId?: string | null;
      projectLabel?: string;
      targetFolderSubPath?: string;
      custodianUserId?: number | null;
      shareWithUserIds?: number[] | null;
    }
  ): Promise<DocumentArchiveDetail> {
    const formData = new FormData();
    if (file && file.size > 0) {
      formData.append('file', file);
    }
    if (options?.documentName?.trim()) {
      formData.append('DocumentName', options.documentName.trim());
    }
    if (options?.projectLabel) {
      formData.append('projectLabel', options.projectLabel);
    }
    if (options?.projectId) {
      formData.append('ProjectId', options.projectId);
    }
    if (options?.targetFolderSubPath) {
      formData.append('TargetFolderSubPath', options.targetFolderSubPath);
    }
    if (options?.custodianUserId != null) {
      formData.append('custodianUserId', String(options.custodianUserId));
    }
    if (options?.shareWithUserIds != null && options.shareWithUserIds.length > 0) {
      formData.append('ShareWithUserIdsJson', JSON.stringify(options.shareWithUserIds));
    }
    const response: AxiosResponse<DocumentArchiveDetail> = await this.api.post('/document-archives/upload', formData);
    return response.data;
  }

  async listDocumentArchiveDriveFolder(path?: string | null): Promise<DriveFolderEntry[]> {
    const response: AxiosResponse<DriveFolderEntry[]> = await this.api.get('/document-archives/drive/children', {
      params: path != null && path.length > 0 ? { path } : {},
    });
    return response.data;
  }

  async createDocumentArchiveDriveFolder(parentPathUnderRoot: string | null | undefined, name: string): Promise<void> {
    await this.api.post('/document-archives/drive/folder', { parentPathUnderRoot: parentPathUnderRoot ?? null, name });
  }

  /**
   * Optional `targetFolderSubPath` is the path under the default archive root (e.g. `Q1/Eng`), same as Add to archive.
   * When omitted, the form field is not sent and the server uses the path already stored on the record.
   */
  async uploadDocumentToCloud(
    id: number,
    file: File,
    options?: { targetFolderSubPath: string }
  ): Promise<DocumentArchiveDetail> {
    const formData = new FormData();
    formData.append('file', file);
    if (options) {
      formData.append('TargetFolderSubPath', options.targetFolderSubPath);
    }
    const response: AxiosResponse<DocumentArchiveDetail> = await this.api.post(`/document-archives/${id}/cloud-upload`, formData);
    return response.data;
  }

  async patchDocumentArchiveCustodian(
    id: number,
    custodianUserId: number | null
  ): Promise<DocumentArchiveDetail> {
    const response: AxiosResponse<DocumentArchiveDetail> = await this.api.patch(
      `/document-archives/${id}/custodian`,
      { custodianUserId }
    );
    return response.data;
  }

  async patchDocumentArchiveMetadata(
    id: number,
    payload: UpdateDocumentArchiveMetadataRequest
  ): Promise<DocumentArchiveDetail> {
    const response: AxiosResponse<DocumentArchiveDetail> = await this.api.patch(
      `/document-archives/${id}/metadata`,
      payload
    );
    return response.data;
  }

  async getDocumentArchiveCustodianLogs(id: number): Promise<DocumentArchiveCustodianLog[]> {
    const response: AxiosResponse<DocumentArchiveCustodianLog[]> = await this.api.get(
      `/document-archives/${id}/custodian-logs`
    );
    return response.data;
  }

  async getDocumentArchiveDownloadUrl(id: number): Promise<{ shareUrl?: string | null; webUrl?: string | null }> {
    const response: AxiosResponse<{ shareUrl?: string | null; webUrl?: string | null }> = await this.api.get(
      `/document-archives/${id}/download-url`
    );
    return response.data;
  }

  async deleteDocumentArchive(id: number): Promise<void> {
    await this.api.delete(`/document-archives/${id}`);
  }

  // Chat
  async getChatStatus(): Promise<ChatStatusDto> {
    const response: AxiosResponse<ChatStatusDto> = await this.api.get('/chat/status');
    return response.data;
  }

  async getChatSkills(): Promise<ChatSkillDto[]> {
    const response: AxiosResponse<ChatSkillDto[]> = await this.api.get('/chat/skills');
    return response.data;
  }

  async listChatSessions(): Promise<ChatSessionDto[]> {
    const response: AxiosResponse<ChatSessionDto[]> = await this.api.get('/chat/sessions');
    return response.data;
  }

  async createChatSession(title?: string, skillIds?: string[]): Promise<ChatSessionDto> {
    const body: CreateChatSessionDto = { title, skillIds };
    const response: AxiosResponse<ChatSessionDto> = await this.api.post('/chat/sessions', body);
    return response.data;
  }

  async updateChatSessionSkills(sessionId: string, skillIds: string[]): Promise<ChatSessionDto> {
    const body: UpdateChatSessionSkillsDto = { skillIds };
    const response: AxiosResponse<ChatSessionDto> = await this.api.put(
      `/chat/sessions/${sessionId}/skills`,
      body
    );
    return response.data;
  }

  async updateChatSessionModel(sessionId: string, model: string): Promise<ChatSessionDto> {
    const body: UpdateChatSessionModelDto = { model };
    const response: AxiosResponse<ChatSessionDto> = await this.api.put(
      `/chat/sessions/${sessionId}/model`,
      body
    );
    return response.data;
  }

  async renameChatSession(sessionId: string, title: string): Promise<ChatSessionDto> {
    const body: RenameChatSessionDto = { title };
    const response: AxiosResponse<ChatSessionDto> = await this.api.put(`/chat/sessions/${sessionId}`, body);
    return response.data;
  }

  async deleteChatSession(sessionId: string): Promise<void> {
    await this.api.delete(`/chat/sessions/${sessionId}`);
  }

  async getChatMessages(
    sessionId: string,
    options?: { limit?: number; before?: string }
  ): Promise<ChatMessagesPageDto> {
    const response: AxiosResponse<ChatMessagesPageDto> = await this.api.get(
      `/chat/sessions/${sessionId}/messages`,
      {
        params: {
          ...(options?.limit != null ? { limit: options.limit } : {}),
          ...(options?.before ? { before: options.before } : {}),
        },
      }
    );
    return response.data;
  }

  async downloadChatAttachment(attachmentId: string, fileName: string): Promise<void> {
    const response = await this.api.get(`/chat/attachments/${attachmentId}/content`, {
      responseType: 'blob',
    });
    const blob = response.data as Blob;
    const url = window.URL.createObjectURL(blob);
    try {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName || 'download';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } finally {
      window.URL.revokeObjectURL(url);
    }
  }

  async sendChatMessage(
    sessionId: string | null,
    message: string,
    files: File[],
    skillIds?: string[],
    model?: string
  ): Promise<ChatSendResponseDto> {
    const formData = new FormData();
    if (sessionId) formData.append('sessionId', sessionId);
    if (message) formData.append('message', message);
    if (skillIds) {
      skillIds.forEach((id) => formData.append('skillIds', id));
    }
    if (model) formData.append('model', model);
    files.forEach((file) => formData.append('files', file, file.name));
    const response: AxiosResponse<ChatSendResponseDto> = await this.api.post('/chat/messages', formData);
    return response.data;
  }
}

export const apiService = new ApiService();
